import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useFoldResource } from '../../../../hooks/useFoldResource';
import { onboardingFlowAPI } from '../../../../services/api';
import { PageHeader, PageState, StatCard } from '../outreach/OutreachShared';
import { focusRing } from '../outreach/outreachUtils';
import { flowQuery, readFlowReport } from './flowAdapter';
import {
  AUDIENCE_OPTIONS,
  BRANCH_KEYS,
  BRANCH_LABELS,
  MILESTONES,
  PLATFORM_OPTIONS,
  VARIANT_OPTIONS,
  barSegments,
  branchRows,
  choiceSplits,
  cohortRows,
  completionRate,
  filterSearch,
  flowHref,
  formatCount,
  formatDay,
  formatDuration,
  formatPercent,
  isEmptyReport,
  localDay,
  openingRows,
  readFilters,
} from './flowFunnel';

const capitalize = (words) => words.charAt(0).toUpperCase() + words.slice(1);

export function ResourceGate({ resource, onRetry, noun, children }) {
  if (resource.status === 'denied') {
    return (
      <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <LockClosedIcon className="mt-0.5 h-6 w-6 flex-none" />
        <p>Fold access is required to see {noun}.</p>
      </div>
    );
  }
  if (resource.status === 'failed') {
    return (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
        <p className="font-semibold">{capitalize(noun)} could not be loaded</p>
        <p className="mt-1 text-sm">{resource.message}</p>
        <button
          type="button"
          onClick={onRetry}
          className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
        >
          Try again
        </button>
      </div>
    );
  }
  if (resource.status !== 'ready') return <PageState loading />;

  return (
    <div
      aria-busy={resource.refreshing ? 'true' : 'false'}
      className={`space-y-6 transition-opacity ${resource.refreshing ? 'opacity-60' : ''}`}
    >
      {resource.refreshError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {capitalize(noun)} could not be loaded: {resource.refreshError} The numbers below are from before the change.
        </div>
      ) : null}
      {children(resource.payload)}
    </div>
  );
}

function readPath(value) {
  return BRANCH_KEYS.includes(value) ? value : BRANCH_KEYS[0];
}

export default function OnboardingFlowPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = localDay();
  const filters = readFilters(searchParams, today);
  const search = filterSearch(filters, today);
  const path = filters.audience === 'all' ? readPath(searchParams.get('path')) : filters.audience;

  const request = useCallback(
    () =>
      onboardingFlowAPI
        .getFlow(flowQuery(readFilters(new URLSearchParams(search), today)))
        .then((response) => readFlowReport(response.data)),
    [search, today],
  );
  const [resource, reload] = useFoldResource(request, 'The onboarding flow could not be loaded.');

  const changeFilters = (changes) => {
    const next = new URLSearchParams(filterSearch({ ...filters, ...changes }, today));
    if (searchParams.get('path')) next.set('path', searchParams.get('path'));
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Onboarding flow"
        description="Every step of the app's onboarding, from Welcome to Home, for flows that started in the range. App events are joined with the server's. Times are Central. Reload the page for the latest numbers."
      />
      <FlowFilters
        filters={filters}
        appVersions={resource.status === 'ready' ? resource.payload.appVersions : []}
        onChange={changeFilters}
      />
      <ResourceGate resource={resource} onRetry={reload} noun="the onboarding flow">
        {(report) =>
          isEmptyReport(report) ? (
            <PageState
              empty
              emptyTitle="No onboarding flows in this range"
              emptyMessage="Try a wider date range or another variant. Development, staging, and production are counted separately."
            />
          ) : (
            <>
              <Totals totals={report.totals} />
              <Funnel report={report} filters={filters} path={path} search={search} today={today} />
              <Choices splits={choiceSplits(report)} />
              <Cohorts rows={cohortRows(report)} />
            </>
          )
        }
      </ResourceGate>
    </div>
  );
}

const FIELD = `mt-1 block min-h-[40px] w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 ${focusRing}`;

function Field({ label, children }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
      {children}
    </label>
  );
}

function FlowFilters({ filters, appVersions, onChange }) {
  const commitVersion = (value) => {
    if (value.trim() !== filters.appVersion) onChange({ appVersion: value.trim() });
  };
  return (
    <form
      aria-label="Filters"
      onSubmit={(event) => event.preventDefault()}
      className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-3 xl:grid-cols-6"
    >
      <Field label="From">
        <input type="date" value={filters.from} max={filters.to} onChange={(event) => event.target.value && onChange({ from: event.target.value })} className={FIELD} />
      </Field>
      <Field label="To">
        <input type="date" value={filters.to} min={filters.from} onChange={(event) => event.target.value && onChange({ to: event.target.value })} className={FIELD} />
      </Field>
      <Field label="Variant">
        <select value={filters.variant} onChange={(event) => onChange({ variant: event.target.value })} className={FIELD}>
          {VARIANT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Path">
        <select value={filters.audience} onChange={(event) => onChange({ audience: event.target.value })} className={FIELD}>
          {AUDIENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="App version">
        <input
          key={filters.appVersion}
          type="text"
          inputMode="decimal"
          list="flow-app-versions"
          defaultValue={filters.appVersion}
          placeholder="All versions"
          onBlur={(event) => commitVersion(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && commitVersion(event.currentTarget.value)}
          className={FIELD}
        />
        <datalist id="flow-app-versions">
          {appVersions.map((version) => (
            <option key={version} value={version} />
          ))}
        </datalist>
      </Field>
      <Field label="Platform">
        <select value={filters.platform} onChange={(event) => onChange({ platform: event.target.value })} className={FIELD}>
          {PLATFORM_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
    </form>
  );
}

function Totals({ totals }) {
  return (
    <section aria-label="Totals" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <StatCard label="Flows started" value={formatCount(totals.started)} />
      <StatCard label="Completed" value={formatCount(totals.completed)} detail="Reached Home" tone="positive" />
      <StatCard label="Completion rate" value={formatPercent(completionRate(totals))} detail="Completed out of started" />
      <StatCard label="Exited to sign-in" value={formatCount(totals.exitedToSignIn)} detail="Signed in to an account they had" />
      <StatCard label="Abandoned" value={formatCount(totals.abandoned)} detail="No event for 14 days" />
      <StatCard label="Stuck now" value={formatCount(totals.stuck)} detail="Last seen 30 minutes to 14 days ago" tone="warning" />
    </section>
  );
}

function SectionHeading({ id, title, children }) {
  return (
    <div>
      <h4 id={id} className="font-semibold text-gray-900">
        {title}
      </h4>
      {children ? <p className="mt-1 text-sm text-gray-600">{children}</p> : null}
    </div>
  );
}

function Funnel({ report, filters, path, search, today }) {
  const pinned = filters.audience !== 'all';
  const linkSearch = (audience) => filterSearch({ ...filters, audience }, today);
  const branchStart = (key) => branchRows(report, key)[0]?.reached ?? 0;

  return (
    <section aria-labelledby="flow-steps" className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <SectionHeading id="flow-steps" title="Step by step">
        Reached counts flows that saw the step. Continued and back are shares of those. The drop compares with the step
        before it on the way through; a side path shows its share instead. Exited here counts flows that ended on the
        step without finishing, whether they signed in instead, skipped, or gave up. Stuck now means the flow&apos;s latest event is
        on this step, from 30 minutes to 14 days ago.
      </SectionHeading>
      <StepTable caption="Opening, every flow" rows={openingRows(report)} linkSearch={linkSearch(filters.audience)} />
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <h5 className="text-sm font-semibold text-gray-900">
          {pinned ? `${BRANCH_LABELS[path]} path` : 'After the split'}
        </h5>
        {pinned ? null : (
          <nav aria-label="Path after the split" className="inline-flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
            {BRANCH_KEYS.map((key) => (
              <Link
                key={key}
                to={flowHref('/onboarding/flow', search, { path: key })}
                aria-current={key === path ? 'page' : undefined}
                className={`inline-flex min-h-[40px] items-center gap-2 rounded-md px-4 text-sm font-medium ${focusRing} ${
                  key === path ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-700 hover:bg-white'
                }`}
              >
                {BRANCH_LABELS[key]}
                <span className={`tabular-nums text-xs ${key === path ? 'text-blue-100' : 'text-gray-500'}`}>
                  {formatCount(branchStart(key))}
                </span>
              </Link>
            ))}
          </nav>
        )}
      </div>
      <StepTable caption={`${BRANCH_LABELS[path]} path`} rows={branchRows(report, path)} linkSearch={linkSearch(path)} />
    </section>
  );
}

const TH = 'px-3 py-2 font-semibold';
const TD = 'px-3 py-2 align-top';
const NUMBER = `${TD} whitespace-nowrap text-right tabular-nums`;
const STEP_COLUMNS = ['w-48', 'w-20', 'w-44', 'w-24', 'w-16', 'w-[4.5rem]', 'w-[4.5rem]', 'w-20', 'w-20', 'w-auto'];

function StepTable({ caption, rows, linkSearch }) {
  const top = Math.max(0, ...rows.map((row) => row.reached));
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full min-w-[68rem] table-fixed text-sm">
        <caption className="sr-only">{caption}</caption>
        <colgroup>
          {STEP_COLUMNS.map((width, index) => (
            <col key={index} className={width} />
          ))}
        </colgroup>
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th scope="col" className={TH}>Step</th>
            <th scope="col" className={`${TH} text-right`}>Reached</th>
            <th scope="col" className={TH}>Drop from step before</th>
            <th scope="col" className={`${TH} text-right`}>Continued</th>
            <th scope="col" className={`${TH} text-right`}>Back</th>
            <th scope="col" className={`${TH} text-right`}>Exited here</th>
            <th scope="col" className={`${TH} text-right`}>Stuck now</th>
            <th scope="col" className={`${TH} text-right`}>Median</th>
            <th scope="col" className={`${TH} text-right`}>p90</th>
            <th scope="col" className={TH}>Top errors</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.step} data-step={row.step} className={row.reached === 0 ? 'text-gray-400' : 'text-gray-900'}>
              <th scope="row" className={`${TD} text-left font-medium ${row.side ? 'pl-8' : ''}`}>
                {row.label}
                <span className="block font-mono text-xs font-normal text-gray-500">{row.step}</span>
              </th>
              <td className={NUMBER}>{formatCount(row.reached)}</td>
              <td className={TD}>
                <DropBar row={row} top={top} />
              </td>
              <td className={NUMBER}>{formatPercent(row.continuedRate)}</td>
              <td className={NUMBER}>{formatPercent(row.backRate)}</td>
              <td className={NUMBER}>
                <CountLink
                  count={row.exitedHere}
                  to={flowHref(`/onboarding/flow/steps/${row.step}/exited`, linkSearch)}
                  label={`exited at ${row.label}`}
                  title={`${formatCount(row.abandonedHere)} gave up, ${formatCount(row.exitedHere - row.abandonedHere)} signed in or skipped`}
                />
              </td>
              <td className={NUMBER}>
                <CountLink count={row.stuckNow} to={flowHref(`/onboarding/flow/steps/${row.step}/stuck`, linkSearch)} label={`stuck on ${row.label}`} />
              </td>
              <td className={NUMBER}>{formatDuration(row.medianMs)}</td>
              <td className={NUMBER}>{formatDuration(row.p90Ms)}</td>
              <td className={TD}>
                <ErrorCodes errors={row.errors} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function dropNote(row) {
  if (row.entry) return row.shareOfBase === null ? '—' : `${formatPercent(row.shareOfBase)} of ${row.base.label}`;
  if (row.dropCount === null) return '—';
  return `−${formatCount(row.dropCount)} · ${formatPercent(row.dropRate)}`;
}

function DropBar({ row, top }) {
  const { kept, dropped } = barSegments(row, top);
  return (
    <div className="min-w-[10rem]" title={row.base ? `Compared with ${formatCount(row.base.count)} at ${row.base.label}` : undefined}>
      <div className="flex h-2 w-40 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <div className="bg-blue-500" style={{ width: `${kept.toFixed(1)}%` }} />
        <div className="bg-red-400" style={{ width: `${dropped.toFixed(1)}%` }} />
      </div>
      <p className={`mt-1 text-xs tabular-nums ${row.dropCount ? 'font-medium text-red-700' : 'text-gray-500'}`}>{dropNote(row)}</p>
    </div>
  );
}

function CountLink({ count, to, label, title }) {
  if (!count) return <span className="text-gray-400">0</span>;
  return (
    <Link
      to={to}
      title={title}
      aria-label={`${formatCount(count)} ${count === 1 ? 'flow' : 'flows'} ${label}`}
      className={`rounded font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900 ${focusRing}`}
    >
      {formatCount(count)}
    </Link>
  );
}

function ErrorCodes({ errors }) {
  if (errors.length === 0) return <span className="text-gray-400">None</span>;
  return (
    <ul className="flex flex-wrap gap-1">
      {errors.map((error) => (
        <li key={error.code} className="break-all rounded border border-red-200 bg-red-50 px-1.5 py-0.5 font-mono text-xs text-red-800">
          {error.code} <span className="font-sans tabular-nums">{formatCount(error.count)}</span>
        </li>
      ))}
    </ul>
  );
}

function Choices({ splits }) {
  if (splits.length === 0) return null;
  return (
    <section aria-labelledby="flow-choices" className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <SectionHeading id="flow-choices" title="Choices">
        Where flows branch, as a share of the flows that saw the step.
      </SectionHeading>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {splits.map((split) => (
          <div key={split.step} className="rounded-lg border border-gray-200 p-3">
            <p className="font-medium text-gray-900">{split.label}</p>
            <p className="text-xs text-gray-500">
              <span className="tabular-nums">{formatCount(split.total)}</span> saw it
            </p>
            <ul className="mt-3 space-y-2">
              {split.options.map((option) => (
                <li key={option.value ?? 'none'} className="text-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-gray-800">{option.label}</span>
                    <span className="tabular-nums text-gray-600">
                      {formatCount(option.count)} <span className="font-semibold text-gray-900">{formatPercent(option.rate)}</span>
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                    <div className="h-full bg-blue-500" style={{ width: `${((option.rate ?? 0) * 100).toFixed(1)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function Cohorts({ rows }) {
  return (
    <section aria-labelledby="flow-cohorts" className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <SectionHeading id="flow-cohorts" title="By the day flows started">
        The share of each day&apos;s flows that reached each milestone so far.
      </SectionHeading>
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">No flows started in this range.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th scope="col" className={TH}>Day</th>
                <th scope="col" className={`${TH} text-right`}>Started</th>
                {MILESTONES.map((milestone) => (
                  <th key={milestone.key} scope="col" className={`${TH} text-right`}>
                    {milestone.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((cohort) => (
                <tr key={cohort.day} data-day={cohort.day}>
                  <th scope="row" className={`${TD} text-left font-medium text-gray-900`}>
                    {formatDay(cohort.day)}
                  </th>
                  <td className={NUMBER}>{formatCount(cohort.started)}</td>
                  {cohort.milestones.map((milestone) => (
                    <td key={milestone.key} className={NUMBER} title={`${formatCount(milestone.count)} of ${formatCount(cohort.started)}`}>
                      {formatPercent(milestone.rate)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
